import React, { createContext, useContext, useState, useEffect } from 'react';

// Create AuthContext
const AuthContext = createContext();

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [initialized, setInitialized] = useState(false);
   const hasValidUserData = (userData) => {
    if (!userData) return false;

    const hasValidName = (userData.fullName && typeof userData.fullName === 'string' && userData.fullName.trim()) ||
                         (userData.name && typeof userData.name === 'string' && userData.name.trim());
    const hasValidEmail = userData.email && typeof userData.email === 'string' && userData.email.trim();
    const hasId = userData._id || userData.id;

    return hasId && (hasValidName || hasValidEmail);
  };

  // Check if user is authenticated - simplified check
  const isAuthenticated = !loading && !!user && !!token && hasValidUserData(user);

  // API base URL
  const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';

  // Helper function to normalize user data structure
  const normalizeUserData = (userData) => {
    if (!userData) return null;

    // console.log('Normalizing user data:', userData);

    let normalizedUser = null;

    // Handle nested user structure: {user: {profile: {...}}}
    if (userData.user?.profile && typeof userData.user.profile === 'object') {
      normalizedUser = {
        ...userData.user.profile,
        _id: userData.user.profile._id || userData.user._id || userData._id,
      };
    }
    // Handle nested user structure: {user: {...}}
    else if (userData.user && typeof userData.user === 'object' && !userData._id && !userData.email) {
      normalizedUser = {
        ...userData.user,
        _id: userData.user._id || userData._id,
      };
    }
    // Handle profile structure: {profile: {...}}
    else if (userData.profile && typeof userData.profile === 'object' && !userData._id && !userData.email) {
      normalizedUser = {
        ...userData.profile,
        _id: userData.profile._id || userData.profile.id || userData._id || userData.id,
        email: userData.profile.email || userData.email,
        fullName: userData.profile.fullName || userData.profile.name || userData.fullName || userData.name,
      };
    }
    // Handle direct user data
    else {
      normalizedUser = userData;
    }

    // console.log('Normalized user:', normalizedUser);
    return normalizedUser;
  };

  // Helper function to check if user has valid data
 

  // Check auth status on component mount
  useEffect(() => {
    if (!initialized) {
      checkAuthStatus();
    }
  }, [initialized]);

  // Function to check current auth status
  const checkAuthStatus = async () => {
    // console.log('🔄 Checking auth status...');
    
    try {
      const storedToken = sessionStorage.getItem('auth_token');
      const storedUser = sessionStorage.getItem('user_data');
      
      if (storedToken && storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          const normalizedUser = normalizeUserData(userData);
          
          if (normalizedUser && hasValidUserData(normalizedUser)) {
            // console.log('✅ Valid auth data found, setting state');
            setUser(normalizedUser);
            setToken(storedToken);
            
            // Verify with backend in background (don't block UI)
            setTimeout(() => verifyTokenWithBackend(storedToken, normalizedUser), 100);
          } else {
            // console.log('❌ Invalid user data, clearing auth');
            clearAuthData();
          }
        } catch (error) {
          console.error('Failed to parse stored auth data:', error);
          clearAuthData();
        }
      } else {
        // console.log('ℹ️ No stored auth data found');
        setUser(null);
        setToken(null);
      }
    } catch (error) {
      console.error('Error during auth status check:', error);
      clearAuthData();
    } finally {
      setLoading(false);
      setInitialized(true);
      // console.log('✅ Auth check completed');
    }
  };

  // Background token verification
  const verifyTokenWithBackend = async (token, currentUser) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        const backendUser = result.data || result.user;
        if (backendUser) {
          const normalizedBackendUser = normalizeUserData(backendUser);
          if (normalizedBackendUser && hasValidUserData(normalizedBackendUser)) {
            // console.log('✅ Token verified, updating user data');
            setUser(normalizedBackendUser);
            sessionStorage.setItem('user_data', JSON.stringify(normalizedBackendUser));
          }
        }
      } else if (response.status === 401) {
        // console.log('🔒 Token expired, clearing auth data');
        clearAuthData();
      }
    } catch (error) {
      // console.log('⚠️ Backend verification failed, keeping stored data:', error.message);
    }
  };

  // Helper function to clear auth data
  const clearAuthData = () => {
    // console.log('🧹 Clearing auth data...');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user_data');
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // Login function
  const login = async (email, password) => {
    try {
      // console.log('🔐 Attempting login for:', email);
      setLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      // console.log('📡 Login response:', data);

      if (response.ok && data.success) {
        const authToken = data.token || data.accessToken;
        const userData = data.user || data.data?.user || data.data;
        
        if (!authToken || !userData) {
          throw new Error('Invalid response structure from server');
        }

        const normalizedUser = normalizeUserData(userData);
        
        if (!normalizedUser || !hasValidUserData(normalizedUser)) {
          throw new Error('Invalid user data received');
        }

        // console.log('✅ Login successful, storing auth data');
        
        // Store in sessionStorage
        sessionStorage.setItem('auth_token', authToken);
        sessionStorage.setItem('token', authToken);
        sessionStorage.setItem('user_data', JSON.stringify(normalizedUser));
        
        // Update state
        setToken(authToken);
        setUser(normalizedUser);
        
        return { success: true, user: normalizedUser, token: authToken };
      } else {
        return { success: false, message: data.message || 'Login failed' };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      return { success: false, message: error.message || 'Network error. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  // Register function (simplified)
  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();
      return response.ok 
        ? { success: true, message: data.message }
        : { success: false, message: data.message || 'Registration failed' };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, message: 'Network error. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // console.log('🚪 Logging out...');
      setLoading(true);
      
      if (token) {
        try {
          await fetch(`${API_BASE_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (error) {
          // console.log('Logout endpoint failed:', error.message);
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthData();
      setLoading(false);
      // console.log('✅ Logout completed');
    }
  };

  // Force refresh auth state
  const refreshAuthState = () => {
    // console.log('🔄 Refreshing auth state...');
    setInitialized(false);
    setLoading(true);
  };

  // Helper functions for components to use
  const getUserData = () => user;
  const getUserEmail = () => user?.email || null;
  const getUserDisplayName = () => user?.fullName || user?.name || (user?.email ? user.email.split('@')[0] : null);
  const getUserInitial = () => {
    const name = getUserDisplayName();
    return name ? name.charAt(0).toUpperCase() : null;
  };

  // Context value
  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    initialized,
    login,
    register,
    logout,
    checkAuthStatus,
    refreshAuthState,
    // Helper functions
    getUserData,
    getUserEmail,
    getUserDisplayName,
    getUserInitial,
    hasValidUserData: () => hasValidUserData(user),
  };

  // console.log(' AuthContext state:', { 
  //   hasUser: !!user,
  //   hasToken: !!token,
  //   isAuthenticated, 
  //   loading,
  //   initialized,
  //   userEmail: getUserEmail()
  // });

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;